import _ from 'underscore';
import { createSelector } from 'reselect';
import schedulesSelector from '../selectors/schedules_selector';
import scheduledMeetingsSelector from './scheduled_meetings_selector';
import teachersSelector from './teachers_selector';
import cohortsSelector from './cohorts_selector';
import navMenuSelector from './nav_menu_selector';
import meetingLayersSelector from './meeting_layers_selector';
import meetingLabelsSelector from './meeting_labels_selector';
import columnSelector from './column_selector';
import ScheduleTable from '../lib/schedule_table/schedule_table';
import Time from '../lib/time';

const classNames = (meetingLabels, columnCount, subColumnCount) => {
  return meetingLabels.reduce((names, label) => {
    if(label.selected) {
      names.push(`abl-meeting-label-${label.id}`);  
    }
    return names;
  }, ['abl-subcols-' + (columnCount * subColumnCount)]);
};

const startTime = createSelector(
	scheduledMeetings => scheduledMeetings,
	scheduledMeetings => {
		let meeting = _.min(scheduledMeetings, (item) => Time.parse(item.start_time).totalMinutes);
		return meeting.start_time;
	}
);

const endTime = createSelector(
	scheduledMeetings => scheduledMeetings,
	scheduledMeetings => {
		let meeting = _.max(scheduledMeetings, (item) => Time.parse(item.end_time).totalMinutes);
		return meeting.end_time;
	}
);

const subColumnCount = columns => {
	return columns.reduce((count, column) => {
		count = Math.max(count, column.subColumns.items.length);
		return count;
	}, 0);
};

const teachersFilter = selectedTeacherIds => scheduledMeeting => {
	return _.intersection(selectedTeacherIds, scheduledMeeting.teacher_ids).length > 0
};

const cohortsFilter = selectedCohortIds => scheduledMeeting => {
  return _.intersection(selectedCohortIds, scheduledMeeting.cohort_ids).length > 0
};

const scheduleFilterGroups = ({type, attribute, values}) =>
  _.map(values, value => {
    return { type, attribute, name: `${value}`, filter: meeting => (meeting[attribute] || []).includes(value) }
  }
);

const getSubColumnFilters = createSelector(
  state => teachersSelector.getSelected(state),
  state => cohortsSelector.getSelected(state),

  (selectedTeachers, selectedCohorts) => {
    let selectedTeacherIds = _.pluck(selectedTeachers, "id");
    let selectedCohortIds = _.pluck(selectedCohorts, "id");

    return _.flatten([
      scheduleFilterGroups({ type: 'teacher', attribute: 'teacher_ids', values: selectedTeacherIds }),
      scheduleFilterGroups({ type: 'cohort', attribute: 'cohort_ids', values: selectedCohortIds })
    ]);
  }
);

const getScheduleFilters = createSelector(
  state => teachersSelector.getSelected(state),
  state => cohortsSelector.getSelected(state),

  (selectedTeachers, selectedCohorts) => {
    let selectedTeacherIds = _.pluck(selectedTeachers, "id");
    let selectedCohortIds = _.pluck(selectedCohorts, "id");

    return [
      {
        join: 'any',
        filters: [
          teachersFilter(selectedTeacherIds),
          cohortsFilter(selectedCohortIds)
        ]
      }
    ];
  }
);

const getViewerTable = createSelector(
  state => state.viewer,
  state => scheduledMeetingsSelector.getAll(state),
  state => scheduledMeetingsSelector.getActive(state, getScheduleFilters(state)),
  state => getSubColumnFilters(state),
  state => teachersSelector.getSelected(state),
  state => cohortsSelector.getSelected(state),
  
  (viewerState, scheduledMeetings, activeMeetings, subColumnFilters, selectedTeachers, selectedCohorts) => {
    let props = viewerState;
    let table = new ScheduleTable(
      Object.assign({
        scheduledMeetings: activeMeetings,
        subColumnFilters: subColumnFilters,
        startTime: startTime(scheduledMeetings),
        endTime: endTime(scheduledMeetings)
      }, props)
    );

    let columns = columnSelector(viewerState, table.columns(), selectedTeachers, selectedCohorts);

    return {
      table: table,
      columns: columns
    };
  }
);

const getLayers = createSelector(
  state => state.controls.layersMenuOpen,
  state => meetingLayersSelector.getAll(state),
  state => meetingLabelsSelector.getAll(state),

  (layersMenuOpen, highlights, labels) => {
    return {
      selected: layersMenuOpen,
      highlights: highlights,
      labels: labels
    };
  }
);

const viewerSelector = createSelector(
  state => state.schedules.loading,
  state => schedulesSelector.getAll(state),
  state => getViewerTable(state),
  state => getLayers(state),
  state => navMenuSelector(state),
  state => state.viewer,
  state => meetingLabelsSelector.getSelected(state),
  state => meetingLayersSelector.getSelected(state),

  (loading, schedules, { table, columns }, layers, scheduleControls, viewerState, meetingLabels, meetingLayers) => {

    let props = {
      loading: loading,
      schedules: schedules,
      table: {
        columnCount: columns.length,
        subColumnCount: subColumnCount(columns),
        rowHeaders: table.rowHeaders(),
        rowHeadersDisplay: table.rowHeaders().length === 0 ? 'none' : null,
        columns: columns,
        display: table.columns().length === 0 ? 'none' : null,
        unHideColumnsButtonDisplay: viewerState.hiddenColumns.length === 0 ? 'none' : null
      },
      controls: scheduleControls,
      layers: layers
    };

    meetingLayersSelector.add(meetingLayers, table.scheduledMeetings);

    props.className = classNames(
      meetingLabels,
      props.table.columnCount,
      props.table.subColumnCount
    ).join(' ');

    return props;
  }
);

export default viewerSelector;


/** WEBPACK FOOTER **
 ** ./src/js/selectors/viewer_selector.js
 **/